import { Check, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { FormEvent, ReactElement, useCallback, useId, useState } from 'react';

import ButtonComponent from '../ButtonComponent';
import ChipListComponent from '../ChipListComponent';
import TextFieldComponent from '../TextFieldComponent';

export interface InputWithChipsProps {
  label: string;
  tags: string[];
  onChangeTags: (newTags: string[]) => void;
  chipIcon?: ReactElement;
  disabled?: boolean;
  placeholder?: string;
  type?: 'text' | 'email' | 'number';
  maxLength?: number;
  maxItems?: number;
  errorMessage?: string;
  formatInput?: (val: string) => string;
  validateInput?: (val: string) => string | null;
  isExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const InputWithChipsComponent = ({
  label,
  tags,
  onChangeTags,
  chipIcon,
  disabled,
  placeholder,
  type = 'text',
  maxLength,
  maxItems,
  errorMessage,
  formatInput,
  validateInput,
  isExpanded = true,
  onExpandChange,
}: InputWithChipsProps) => {
  const id = useId();
  const [inputValue, setInputValue] = useState<string>('');
  const [internalError, setInternalError] = useState<string | null>(null);
  const [internalExpanded, setInternalExpanded] = useState(true);

  const isControlled = onExpandChange !== undefined;
  const expandedState = isControlled ? isExpanded : internalExpanded;

  const handleToggleExpanded = useCallback(() => {
    if (isControlled) {
      onExpandChange(!expandedState);  
    } else {
      setInternalExpanded(!expandedState);
    }
  }, [expandedState, isControlled, onExpandChange]);

  const handleClearAll = useCallback(() => {
    onChangeTags([]);
    if (!isControlled) {
      setInternalExpanded(false);
    }
  }, [onChangeTags, isControlled]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    let val = inputValue.trim();

    if (!val) return;

    if (formatInput) val = formatInput(val);

    if (validateInput) {
      const error = validateInput(val);
      if (error) {
        setInternalError(error);
        return;
      }
    }

    if (tags.includes(val)) {
      setInternalError('Este item já foi adicionado');
      return;
    }

    if (maxItems && tags.length >= maxItems) {
      setInternalError(`Máximo de ${maxItems} itens permitidos`);
      return;
    }

    onChangeTags([...tags, val]);
    setInputValue('');
    setInternalError(null);

    if (isControlled) {
      onExpandChange(true);
    } else {
      setInternalExpanded(true);
    }
  };

  const handleInputChange = (val: string) => {
    if (maxLength && val.length > maxLength) return;
    setInputValue(val);
    if (internalError) setInternalError(null);
  };

  const handleDelete = useCallback(
    (index: number) => {
      const newTags = tags.filter((_, i) => i !== index);
      onChangeTags(newTags);
    },
    [tags, onChangeTags]
  );

  const displayError = internalError || errorMessage;

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}
      >
        <TextFieldComponent
          label={label}
          value={inputValue}
          onChangeText={handleInputChange}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          mb={0}
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              borderBottomRightRadius: 0,
              borderTopRightRadius: 0,
            },
          }}
        />

        <ButtonComponent
          disabled={disabled || !inputValue.trim()}
          type="submit"
          variant="contained"
          color="primary"
          sx={{
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            boxShadow: 'none',
            minWidth: '56px',
            height: '56px',
          }}
        >
          <Check />
        </ButtonComponent>
      </Box>

      {displayError && (
        <Typography fontSize={12} color="error" sx={{ mt: 0.5, ml: 1.5 }}>
          {displayError}
        </Typography>
      )}

      {tags.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleToggleExpanded}
              endIcon={expandedState ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              sx={{
                textTransform: 'none',
                color: 'gray',
                p: 0,
                '&:hover': { background: 'transparent', color: '#fff' },
              }}
            >
              {expandedState ? 'Ocultar itens' : `Ver ${tags.length} adicionado(s)`}
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={handleClearAll}
              disabled={disabled}
              sx={{
                textTransform: 'none',
                color: '#ff4d4d',
                p: 0,
                '&:hover': { background: 'transparent', color: '#ff1a1a' },
              }}
            >
              Limpar todos
            </Button>
          </Box>

          <Collapse in={expandedState}>
            <Box sx={{ mt: 1 }}>
              <ChipListComponent chipIcon={chipIcon} chipData={tags} onDelete={handleDelete} />
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default InputWithChipsComponent;